import { useCallback, useEffect, useState, type ReactNode } from "react";
import { RolagemLembrada } from "./componentes/RolagemLembrada.js";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import type { SessaoAtual } from "@noesis/shared";
import { FalhaDaApi, aoExigirNovaSenha, pedir } from "./api.js";
import { useAlturaPublicada } from "./altura-medida.js";
import { EstadoOffline } from "./componentes/EstadoOffline.js";
import { BarraLateral, CabecalhoMovel, NavegacaoInferior, useLateral } from "./componentes/Navegacao.js";
import { RitualProvider } from "./estado/ritual.js";
import { SessaoProvider } from "./estado/sessao.js";
import { TrilhasProvider } from "./estado/trilhas.js";
import { TourProvider } from "./estado/tour.js";
import { TourGuiado } from "./componentes/tour/Tour.js";
import { Acervo } from "./telas/Acervo.js";
import { Entrar } from "./telas/Entrar.js";
import { Numeros } from "./telas/Numeros.js";
import { Preferencias } from "./telas/Preferencias.js";
import { Estudar } from "./telas/Estudar.js";
import { Caderno, PaginaNova } from "./telas/Caderno.js";
import { Revisar } from "./telas/Revisar.js";
import { Simulado } from "./telas/Simulado.js";
import { Inicio } from "./telas/Inicio.js";
import { RedefinirSenha } from "./telas/RedefinirSenha.js";
import { TrilhaFormulario } from "./telas/TrilhaFormulario.js";
import { aplicarTema, temaGuardado } from "./tema.js";

/**
 * Quatro estados, sempre (invariante 3). "carregando" nunca é permanente:
 * qualquer falha vira "erro" com uma frase e um botão, nunca um giro eterno.
 *
 * O roteador só existe depois da sessão. Enquanto não há usuário não há
 * endereço para navegar — e assim nenhuma tela interna precisa se defender de
 * `sessao` nula.
 */
type Estado =
  | { nome: "carregando" }
  | { nome: "fora" }
  | { nome: "dentro"; sessao: SessaoAtual }
  /**
   * Conta travada até definir a senha. É um estado da raiz, e não uma rota:
   * enquanto ele vale **o roteador nem existe**, então não há endereço que
   * escape — nem colado na barra, nem guardado no histórico. Pela mesma razão
   * não há laço a temer: a tela não navega para lugar nenhum.
   */
  | { nome: "redefinir"; sessao: SessaoAtual }
  | { nome: "erro"; mensagem: string };

export function App() {
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });

  useEffect(() => {
    aplicarTema(temaGuardado());
  }, []);

  const conferirSessao = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      const sessao = await pedir("sessao.atual");
      aplicarTema(sessao.usuario.tema);
      setEstado({ nome: sessao.usuario.deveRedefinirSenha ? "redefinir" : "dentro", sessao });
    } catch (e) {
      if (e instanceof FalhaDaApi && e.codigo === "nao_autenticado") {
        setEstado({ nome: "fora" });
        return;
      }
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível falar com o Grifo.",
      });
    }
  }, []);

  useEffect(() => {
    void conferirSessao();
  }, [conferirSessao]);

  /*
    A segunda metade da interceptação — a que cobre a trava que **liga no meio
    da sessão**.

    A leitura de `sessao.atual` só acontece na subida: uma aba aberta desde
    ontem não a refaz. Sem isto, essa aba continuaria batendo em 403 sem que
    nada na tela explicasse por quê. O aviso vem do cliente de API, que é por
    onde toda chamada passa, e aqui ele vira o mesmo estado da raiz.
  */
  useEffect(() => {
    aoExigirNovaSenha(() => {
      setEstado((atual) => (atual.nome === "dentro" ? { nome: "redefinir", sessao: atual.sessao } : atual));
    });
    return () => aoExigirNovaSenha(null);
  }, []);

  /*
    As duas barras flutuantes, medidas e publicadas na raiz. Elas cobrem o fim
    do fluxo — é assim que a quinta alternativa da execução ficava atrás da
    barra de ação —, e a altura de cada uma muda com a largura, com a quebra do
    rótulo e com a área segura do aparelho. Ver `altura-medida.ts`.

    Fica aqui, e não em cada tela: a barra aparece em dez delas e a navegação
    troca de forma com a largura. Dez pontos de publicação seriam dez pontos
    para esquecer um.
  */
  useAlturaPublicada(".barra-acao", "--altura-barra-acao");

  if (estado.nome === "carregando") {
    return (
      <div className="tela">
        <div className="tela__corpo" aria-busy="true">
          <span className="so-leitor" role="status">
            Carregando o Grifo
          </span>
          <div className="esqueleto esqueleto--titulo" />
          <div className="esqueleto esqueleto--bloco" />
          <div className="esqueleto esqueleto--faixa" />
        </div>
      </div>
    );
  }

  if (estado.nome === "erro") {
    return (
      <div className="tela">
        <div className="tela__corpo">
          <div className="estado estado--erro" role="alert">
            <h1 className="estado__titulo">O Grifo não respondeu</h1>
            <p className="estado__texto">{estado.mensagem}</p>
            <button type="button" className="botao" onClick={() => void conferirSessao()}>
              Tentar de novo
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (estado.nome === "fora") {
    return (
      <Entrar
        aoEntrar={(sessao) =>
          setEstado({ nome: sessao.usuario.deveRedefinirSenha ? "redefinir" : "dentro", sessao })
        }
      />
    );
  }

  if (estado.nome === "redefinir") {
    return (
      <RedefinirSenha
        usuario={estado.sessao.usuario}
        /*
          A troca derruba **todas** as sessões da conta, inclusive a que fez o
          pedido — e o servidor devolve uma sessão nova no mesmo 200, com o
          cookie já plantado. Por isso o estado vem da resposta e não de um
          `sessao.atual` logo depois: entre uma coisa e outra caberia um 401 na
          cara de quem acabou de acertar a senha.
        */
        aoTrocar={(sessao) => {
          aplicarTema(sessao.usuario.tema);
          setEstado({ nome: "dentro", sessao });
        }}
        aoSair={() => {
          void pedir("sessao.sair").catch(() => {
            /* sair é local de qualquer jeito: o cookie já não serve para nada */
          });
          setEstado({ nome: "fora" });
        }}
      />
    );
  }

  const sair = () => setEstado({ nome: "fora" });

  return (
    <BrowserRouter>
      <SessaoProvider sessao={estado.sessao}>
        <TrilhasProvider>
          {/*
            O ritual fica **dentro** do provedor de trilhas: a ofensiva é por
            trilha (RN-01), e é a troca da trilha ativa que manda relê-la.
          */}
          <RitualProvider>
          <TourProvider>
          {/*
            A moldura do Caderno (ADR 040): a folha é uma grade de duas colunas,
            a lateral com os furos e o `main` com a margem vermelha. No celular
            a lateral some, e quem navega é a ilha. A media query decide a
            forma sem estado, e nenhuma das duas pisca ao redimensionar.
          */}
          <Moldura>
              <Routes>
                <Route path="/" element={<Inicio />} />
                <Route path="/estudar" element={<Estudar />} />
                <Route path="/revisar" element={<Revisar />} />
                <Route path="/simulado" element={<Simulado />} />
                <Route path="/estatisticas" element={<Numeros />} />
                {/*
                  As três rotas do caderno são a **mesma tela** desde a Fase 2:
                  árvore à esquerda, editor à direita. Sem id ela abre a última
                  página editada; `/caderno/nova` continua sendo o endereço que
                  o "Não entendi" usa para abrir um esboço — ele cria a página
                  com o que veio na URL e manda para ela.
                */}
                <Route path="/caderno" element={<Caderno />} />
                <Route path="/caderno/nova" element={<PaginaNova />} />
                <Route path="/caderno/:id" element={<Caderno />} />
                <Route path="/acervo" element={<Acervo />} />
                <Route path="/ajustes" element={<Preferencias aoSair={sair} />} />
                <Route path="/trilhas/nova" element={<TrilhaFormulario />} />
                <Route path="/trilhas/:id/editar" element={<TrilhaFormulario />} />
                {/*
                  Os caminhos das telas que viraram aba continuam existindo,
                  como redirecionamento (RN-30). Há links internos apontando
                  para eles — e favoritos: quebrar uma URL que funcionava é
                  custo que a fusão não precisa cobrar.
                */}
                <Route path="/diagnostico" element={<Navigate to="/estatisticas?aba=diagnostico" replace />} />
                <Route path="/trilhas" element={<Navigate to="/acervo?aba=trilhas" replace />} />
                <Route path="/importar" element={<Navigate to="/acervo?aba=importar" replace />} />
                <Route path="/fontes" element={<Navigate to="/acervo?aba=fontes" replace />} />
                <Route path="/conta" element={<Navigate to="/ajustes?aba=conta" replace />} />
                {/* Endereço desconhecido volta para o início em vez de mostrar
                    um 404 dentro de um app de uma pessoa só. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
          </Moldura>
          <TourGuiado />
          </TourProvider>
          </RitualProvider>
        </TrilhasProvider>
      </SessaoProvider>
    </BrowserRouter>
  );
}

function Moldura({ children }: { children: ReactNode }) {
  const [recolhida, alternar] = useLateral();
  return (
    <div className={recolhida ? "folha folha--recolhida" : "folha"}>
      <BarraLateral recolhida={recolhida} aoAlternar={alternar} />
      <main className="folha__main" id="conteudo">
        <CabecalhoMovel />
        <EstadoOffline />
        {/* Voltar devolve a rolagem; navegar vai para o topo (Fase 6A). */}
        <RolagemLembrada />
        {children}
      </main>
      <NavegacaoInferior />
    </div>
  );
}
